import React, { memo } from 'react';
import { Step, Block } from '@/types/Document';
import BlockRenderer from './BlockRenderer';
import ItemActionMenu from './ItemActionMenu';

interface StepRendererProps {
  step: Step;
  flowIndex: number;
  phaseIndex: number;
  stepIndex: number;
  editMode: boolean;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
}

const StepRenderer: React.FC<StepRendererProps> = memo(({
  step,
  flowIndex,
  phaseIndex,
  stepIndex,
  editMode,
  openMenuPath,
  setOpenMenuPath,
  handleInputChange,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
}) => {
  const stepPath = `step-${flowIndex}-${phaseIndex}-${stepIndex}`;
  const isMenuOpen = openMenuPath === stepPath;

  return (
    <div className="bg-blue-50 border border-gray-200 rounded-lg p-2.5">
      <div className="flex justify-between items-center mb-1.5">
        <div className="flex-grow mr-3">
          {editMode ? (
            <input
              type="text"
              value={step.name}
              onChange={(e) => 
                handleInputChange(
                  [flowIndex, 'phases', phaseIndex, 'steps', stepIndex, 'name'], 
                  e.target.value, 
                  'step'
                )
              }
              className="w-full text-sm font-medium text-gray-700 p-1 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <h5 className="text-sm font-medium text-gray-700">{step.name}</h5>
          )}
        </div>
        <div className="flex space-x-1 text-gray-400 relative flex-shrink-0">
          {editMode && (
            <button 
              type="button"
              onClick={() => setOpenMenuPath(isMenuOpen ? null : stepPath)}
              className="p-1 rounded hover:bg-gray-200"
              title="Step Actions"
              aria-label="Step actions menu"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth={2}
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" 
                />
              </svg>
            </button>
          )}
          {isMenuOpen && (
            <ItemActionMenu 
              itemPath={stepPath}
              itemType="step"
              onClose={() => setOpenMenuPath(null)}
              onAddItem={handleAddItem}
              onDeleteItem={handleDeleteItem}
              onChangeItemType={handleChangeItemType}
              onCreateStepFromSelection={handleCreateStepFromSelection}
            />
          )}
        </div>
      </div>
      
      {/* Blocks Rendering */}
      {step.blocks && (
        <div className="space-y-2">
          {Object.entries(step.blocks).map(([blockKey, blockContent]) => {
            if (!blockContent) return null;
            const block = blockContent as Block;
            const blockPath = `block-${flowIndex}-${phaseIndex}-${stepIndex}-${blockKey}`;
            
            return (
              <BlockRenderer
                key={blockPath}
                block={block}
                blockKey={blockKey}
                blockPath={blockPath}
                flowIndex={flowIndex}
                phaseIndex={phaseIndex}
                stepIndex={stepIndex}
                editMode={editMode}
                openMenuPath={openMenuPath}
                setOpenMenuPath={setOpenMenuPath}
                handleInputChange={handleInputChange}
                handleDeleteItem={handleDeleteItem}
                handleAddItem={handleAddItem}
                handleChangeItemType={handleChangeItemType}
                handleCreateStepFromSelection={handleCreateStepFromSelection}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

StepRenderer.displayName = 'StepRenderer';

export default StepRenderer;
